import { useAuth } from './AuthContext';

/**
 * Central permissions hook.
 *
 * Permission model (mirrors the backend):
 *   - is_staff || is_superuser  →  full access (isAdmin = true, hasPerm always returns true)
 *   - regular authenticated user →  access based on Django model permissions
 *     user.permissions contains ['app_label.codename', ...] from get_all_permissions()
 *
 * Usage:
 *   const { canManage, isAdmin, hasPerm } = usePermissions();
 *   {hasPerm('projects.view_project') && <Link to="/projects">Projects</Link>}
 *   {hasPerm('projects.add_project') && <button>Add project</button>}
 */

interface PermissionsResult {
  /** True for staff/superuser — maps to backend IsAdminUser */
  isAdmin: boolean;
  /** Alias for isAdmin — whether the user may create/edit/delete */
  canManage: boolean;
  /** Check for a specific Django permission string, e.g. 'projects.view_project' */
  hasPerm: (perm: string) => boolean;
}

export const usePermissions = (): PermissionsResult => {
  const { user } = useAuth();

  const isAdmin = !!(user?.is_staff || user?.is_superuser);

  const hasPerm = (perm: string): boolean => {
    if (isAdmin) return true;
    return (user?.permissions || []).includes(perm);
  };

  return {
    isAdmin,
    canManage: isAdmin,
    hasPerm,
  };
};
